import { NgPageHeader, PsapFeatureView } from '@/ui';
import { toRowFromIncident } from '@/features/shared/incidentListRow';
import { usePsapConfiguration } from '@/app/shell/psapConfiguration';
import type { CallNotificationItem, Incident } from '@/signalr/contracts';
import type { IncidentListContext } from './incidentListContext';
import {
  selectCurrentlyOpenIncidents,
  selectForwardedIncidents,
  selectRecentClosedIncidents,
  toCallsWithIncidentId,
} from './incidentListSelectors';
import { IncidentListPageList } from './IncidentListPageList';
import { IncidentListTab } from './IncidentListTab';

export type IncidentListContentProps = {
  isLoading?: boolean;
  incidents?: readonly Incident[];
  callNotifications?: readonly CallNotificationItem[];
  onTabSwitch?: (context: IncidentListContext) => void;
};

export function IncidentListContent({
  isLoading = true,
  incidents,
  callNotifications,
  onTabSwitch,
}: IncidentListContentProps) {
  const psapId = usePsapConfiguration().id;

  const forwarded = selectForwardedIncidents(incidents, psapId);
  const currentlyOpen = selectCurrentlyOpenIncidents(incidents, psapId);
  const recentClosed = selectRecentClosedIncidents(incidents, psapId);

  const callNotificationsItems = toCallsWithIncidentId(callNotifications, currentlyOpen, forwarded);

  const forwardedIncidents = forwarded.map(toRowFromIncident);
  const currentlyOpenIncidents = currentlyOpen.map(toRowFromIncident);
  const recentClosedIncidents = recentClosed.map(toRowFromIncident);

  const incidentsCount = forwarded.length + currentlyOpen.length;
  const totalNotificationsCount = incidentsCount + (callNotifications?.length ?? 0);

  return (
    <PsapFeatureView
      emergencyCallNotification
      whenEnabled={
        <>
          <NgPageHeader title={`(${totalNotificationsCount})`} separator=" " />
          <IncidentListTab
            isLoading={isLoading}
            forwardedIncidents={forwardedIncidents}
            currentlyOpenIncidents={currentlyOpenIncidents}
            recentClosedIncidents={recentClosedIncidents}
            callNotifications={callNotificationsItems}
            onTabSwitch={onTabSwitch}
          />
        </>
      }
      whenDisabled={
        <>
          <NgPageHeader title={`(${incidentsCount})`} separator=" " />
          <IncidentListPageList
            forwardedIncidents={forwardedIncidents}
            currentlyOpenIncidents={currentlyOpenIncidents}
            recentClosedIncidents={recentClosedIncidents}
            isLoading={isLoading}
          />
        </>
      }
    />
  );
}
